import { useCallback, useEffect, useRef, useState } from "react";
import { Image as ImageIcon, Paperclip, Trash2, Upload } from "lucide-react";
import {
  EMPLOYER_IMAGE_KIND_LOGO,
  EMPLOYER_IMAGE_KIND_COVER,
  uploadEmployerImage,
  uploadEmployerDocument,
  deleteEmployerDocument,
  getEmployerImageBlob,
} from "./thailandJobsApi.js";

/**
 * P161: the employer's own media - company logo, cover/background image, and any
 * number of attached documents. Owner: "เพิ่มแนบ logo บริษัท / เพิ่มแนบ รูปพื้หลัง /
 * ให้แนบเอกสารได้".
 *
 * Only rendered when editing, never when creating, and that is a real
 * constraint rather than a layout choice: all three endpoints are keyed by
 * employer id, so until the employer exists there is nothing to attach to. The
 * create form says so in one line instead of showing controls that could not
 * work yet.
 *
 * Every upload here is its own request, applied immediately - not queued behind
 * the form's Save. An operator who picks a logo sees it land; nothing about the
 * company's details is touched by doing so.
 */

const MEDIA_HINT = "รองรับไฟล์รูปแบบ JPG, PNG หรือ WEBP ขนาดไม่เกิน 8 MB";

/** Human file size. Bytes are what the server records; nobody reads 2097152. */
function formatSizeBytes(sizeBytes) {
  if (sizeBytes == null) {
    return null;
  }
  if (sizeBytes < 1024) {
    return `${sizeBytes} B`;
  }
  if (sizeBytes < 1024 * 1024) {
    return `${Math.round(sizeBytes / 1024)} KB`;
  }
  return `${(sizeBytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatUploadedAt(value) {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return date.toLocaleDateString("th-TH-u-ca-gregory", { day: "2-digit", month: "short", year: "numeric" });
}

/**
 * Operator-facing wording for a failed attachment. A 400 is the server's own
 * validated refusal (wrong type or too large) and its message already names
 * which file to pick instead, so it is passed through rather than replaced.
 */
function describeMediaError(error) {
  if (error?.status === 400 && error?.body?.message) {
    return error.body.message;
  }
  if (error?.status === 401) {
    return "เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่";
  }
  if (error?.status === 403) {
    return "คุณไม่มีสิทธิ์แนบไฟล์ให้นายจ้างรายนี้";
  }
  if (error?.status === 413) {
    return "ไฟล์ใหญ่เกินไป กรุณาใช้ไฟล์ที่มีขนาดไม่เกิน 8 MB";
  }
  return "ไม่สามารถแนบไฟล์ได้ กรุณาลองใหม่อีกครั้ง";
}

/**
 * One stored image, fetched with the session token and shown from an object URL.
 * The endpoint is authenticated, so an `<img src>` pointed straight at it would
 * 401. The URL is revoked on unmount and whenever the image changes, so a drawer
 * opened repeatedly does not leak one blob per open.
 *
 * `hasImage` comes from the record the caller already holds, so an employer with
 * no logo costs no request rather than a guaranteed 404.
 */
function StoredImage({ employerId, imageKind, hasImage, reloadToken, variant, alt, emptyText }) {
  const [objectUrl, setObjectUrl] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!employerId || !hasImage) {
      setObjectUrl(null);
      setFailed(false);
      return undefined;
    }

    let cancelled = false;
    let created = null;
    setFailed(false);

    getEmployerImageBlob(employerId, imageKind)
      .then(({ blob }) => {
        if (cancelled) {
          return;
        }
        // jsdom has no createObjectURL; treat its absence as "cannot preview"
        // rather than crashing the drawer around it.
        if (typeof URL === "undefined" || typeof URL.createObjectURL !== "function") {
          setFailed(true);
          return;
        }
        created = URL.createObjectURL(blob);
        setObjectUrl(created);
      })
      .catch(() => {
        if (!cancelled) {
          setFailed(true);
        }
      });

    return () => {
      cancelled = true;
      if (created && typeof URL !== "undefined" && typeof URL.revokeObjectURL === "function") {
        URL.revokeObjectURL(created);
      }
    };
  }, [employerId, imageKind, hasImage, reloadToken]);

  const className = `wx-tj-media-preview wx-tj-media-preview--${variant}`;

  if (objectUrl) {
    return <img className={className} src={objectUrl} alt={alt} />;
  }

  return (
    <div className={`${className} wx-tj-media-preview--empty`} role="img" aria-label={hasImage && failed ? "ไม่สามารถแสดงรูปได้" : emptyText}>
      <ImageIcon size={variant === "cover" ? 22 : 18} aria-hidden="true" />
      <span>{hasImage && failed ? "แสดงรูปไม่ได้" : emptyText}</span>
    </div>
  );
}

/** One image slot: what is stored now, and a control to replace it. */
function ImageSlot({ employerId, imageKind, label, hint, variant, hasImage, emptyText, disabled, busy, onPick }) {
  const inputRef = useRef(null);
  const inputId = `wx-tj-media-${imageKind}`;

  return (
    <div className="wx-tj-media-slot">
      <div className="wx-tj-media-slot-head">
        <label htmlFor={inputId}>{label}</label>
        <p className="wx-tj-field-note">{hint}</p>
      </div>
      <StoredImage
        employerId={employerId}
        imageKind={imageKind}
        hasImage={hasImage}
        reloadToken={busy}
        variant={variant}
        alt={label}
        emptyText={emptyText}
      />
      <input
        id={inputId}
        ref={inputRef}
        type="file"
        aria-label={label}
        // The same three types the server's own allow-list accepts. This only
        // filters the file dialog - the real check is the server's magic-number
        // test, which this can never replace.
        accept="image/jpeg,image/png,image/webp"
        disabled={disabled}
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) {
            onPick(file);
          }
          // Cleared so picking the same file twice still fires a change.
          if (inputRef.current) {
            inputRef.current.value = "";
          }
        }}
      />
    </div>
  );
}

export default function EmployerMediaSection({ employer, onChanged }) {
  const employerId = employer?.employerId;
  const [busyToken, setBusyToken] = useState(0);
  const [pending, setPending] = useState(null);
  const [error, setError] = useState(null);
  const [documentType, setDocumentType] = useState("");
  const documentInputRef = useRef(null);

  const documents = employer?.documents ?? [];

  /**
   * Every write here re-reads the employer through its caller, so the panel
   * always renders what the server actually stored rather than an optimistic
   * guess that could disagree with it.
   */
  const run = useCallback(
    async (what, action) => {
      setPending(what);
      setError(null);
      try {
        const updated = await action();
        setBusyToken((token) => token + 1);
        onChanged?.(updated);
      } catch (caught) {
        setError(describeMediaError(caught));
      } finally {
        setPending(null);
      }
    },
    [onChanged],
  );

  if (!employerId) {
    return (
      <section className="wx-tj-formsec">
        <header className="wx-tj-formsec-head">
          <span className="wx-tj-formsec-icon" aria-hidden="true">
            <ImageIcon size={16} />
          </span>
          <div>
            <h3>โลโก้ รูปพื้นหลัง และเอกสาร</h3>
            <p>แนบได้หลังจากบันทึกนายจ้างรายนี้แล้ว</p>
          </div>
        </header>
        <div className="wx-tj-formsec-body">
          <p className="wx-tj-field-note">
            ไฟล์ทุกอย่างผูกกับนายจ้างรายนี้ จึงยังแนบไม่ได้จนกว่าจะกดสร้างนายจ้างเสร็จ — สร้างเสร็จแล้วเปิดรายการนี้ขึ้นมาแก้ไขเพื่อแนบไฟล์ได้ทันที
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="wx-tj-formsec">
      <header className="wx-tj-formsec-head">
        <span className="wx-tj-formsec-icon" aria-hidden="true">
          <ImageIcon size={16} />
        </span>
        <div>
          <h3>โลโก้ รูปพื้นหลัง และเอกสาร</h3>
          <p>ไฟล์ที่แนบจะถูกบันทึกทันทีที่เลือก ไม่ต้องกดบันทึกฟอร์มอีกครั้ง</p>
        </div>
      </header>

      <div className="wx-tj-formsec-body">
        <div className="wx-tj-media-slots">
          <ImageSlot
            employerId={employerId}
            imageKind={EMPLOYER_IMAGE_KIND_LOGO}
            label="โลโก้บริษัท"
            hint={MEDIA_HINT}
            variant="logo"
            hasImage={Boolean(employer?.logo)}
            emptyText="ยังไม่มีโลโก้"
            disabled={pending !== null}
            busy={busyToken}
            onPick={(file) => run("logo", () => uploadEmployerImage(employerId, EMPLOYER_IMAGE_KIND_LOGO, file))}
          />
          <ImageSlot
            employerId={employerId}
            imageKind={EMPLOYER_IMAGE_KIND_COVER}
            label="รูปพื้นหลัง"
            hint={MEDIA_HINT}
            variant="cover"
            hasImage={Boolean(employer?.coverImage)}
            emptyText="ยังไม่มีรูปพื้นหลัง"
            disabled={pending !== null}
            busy={busyToken}
            onPick={(file) => run("cover", () => uploadEmployerImage(employerId, EMPLOYER_IMAGE_KIND_COVER, file))}
          />
        </div>

        <div className="wx-tj-media-docs">
          <div className="wx-tj-media-slot-head">
            <label htmlFor="wx-tj-media-document">เอกสารแนบ</label>
            <p className="wx-tj-field-note">
              เช่น หนังสือรับรองบริษัท ภ.พ.20 แผนที่ หรือสัญญา — ใส่ชื่อประเภทเอกสารก่อนเลือกไฟล์ เพื่อให้คนอื่นรู้ว่าไฟล์นี้คืออะไร
            </p>
          </div>

          <div className="wx-tj-media-doc-picker">
            <input
              type="text"
              aria-label="ประเภทเอกสาร"
              placeholder="ประเภทเอกสาร เช่น หนังสือรับรองบริษัท"
              maxLength={120}
              value={documentType}
              onChange={(event) => setDocumentType(event.target.value)}
              disabled={pending !== null}
            />
            <input
              id="wx-tj-media-document"
              ref={documentInputRef}
              type="file"
              aria-label="เอกสารแนบ"
              accept="image/jpeg,image/png,image/webp"
              disabled={pending !== null}
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (documentInputRef.current) {
                  documentInputRef.current.value = "";
                }
                if (!file) {
                  return;
                }
                const label = documentType.trim();
                run("document", async () => {
                  const updated = await uploadEmployerDocument(employerId, file, label || null);
                  setDocumentType("");
                  return updated;
                });
              }}
            />
          </div>

          {documents.length === 0 ? (
            <p className="wx-tj-field-note">ยังไม่มีเอกสารแนบสำหรับนายจ้างรายนี้</p>
          ) : (
            <ul className="wx-tj-media-doc-list">
              {documents.map((document) => {
                const size = formatSizeBytes(document.sizeBytes);
                const uploadedAt = formatUploadedAt(document.uploadedAtUtc);
                return (
                  <li key={document.documentId}>
                    <span className="wx-tj-media-doc-icon" aria-hidden="true">
                      <Paperclip size={14} />
                    </span>
                    <span className="wx-tj-media-doc-text">
                      <strong>{document.documentType || document.fileName || "เอกสารแนบ"}</strong>
                      <span>{[document.fileName, size, uploadedAt].filter(Boolean).join(" · ")}</span>
                    </span>
                    <button
                      type="button"
                      className="wx-tj-media-doc-remove"
                      aria-label={`ลบเอกสาร ${document.documentType || document.fileName || document.documentId}`}
                      disabled={pending !== null}
                      onClick={() => run("delete", () => deleteEmployerDocument(employerId, document.documentId))}
                    >
                      <Trash2 size={14} aria-hidden="true" />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="wx-tj-media-status" role="status">
          {pending !== null && (
            <span className="wx-tj-media-busy">
              <Upload size={13} aria-hidden="true" /> กำลังอัปโหลด...
            </span>
          )}
        </div>

        {error && (
          <p className="wx-tj-form-message" role="alert">
            {error}
          </p>
        )}
      </div>
    </section>
  );
}
